/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useParams } from 'react-router'
import PageTitle from '../../../../client/js/components/PageTitle'
import useQueryParams from '../../../../client/js/hooks/useQueryParams'
import { fetchTransaction } from '../../lib/rest/transactionAPIClient'
import TransactionForm from './TransactionForm'
import { useQuery } from '@tanstack/react-query'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import { Card, Container } from '@mantine/core'
import Page from '../../../../client/js/components/Page'
import BreadcrumbCard from '../../../../client/js/components/breadcrumb/BreadcrumbCard'
import { getTransactionListLink } from '../../lib/links/transactionLinks'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import AdminBreadcrumb from '../../../../client/js/components/breadcrumb/AdminBreadcrumb'

export type TransactionFormPageProps = {
  expense: boolean
}

function TransactionFormPage (props: TransactionFormPageProps) {
  const { expense } = props
  const intl = useIntl()
  const params = useParams<{ id?: string }>()
  const [searchParams] = useQueryParams()
  const transactionId = parseInt(params.id, 10)

  const transactionQuery = useQuery({
    enabled: true,
    queryKey: [
      'transaction',
      expense,
      transactionId,
      searchParams
    ],
    queryFn: () => transactionId > 0
      ? fetchTransaction(transactionId)
      : Promise.resolve(({
        expense,
        category: searchParams.category,
        customerId: typeof searchParams.customerId === 'string'
          ? parseInt(searchParams.customerId, 10)
          : undefined,
        estateId: typeof searchParams.estateId === 'string'
          ? parseInt(searchParams.estateId, 10)
          : undefined,
        leaseId: typeof searchParams.leaseId === 'string'
          ? parseInt(searchParams.leaseId, 10)
          : undefined,
        interventionId: typeof searchParams.interventionId === 'string'
          ? parseInt(searchParams.interventionId, 10)
          : undefined
      }) as Partial<Transaction>),
    refetchOnWindowFocus: false
  })
  const { data } = transactionQuery

  const title = useMemo(() => {
    if (data && data.id > 0) {
      if (data.label != null) {
        return `${params.id} - ${data.label}`
      }
      return intl.formatMessage({
        id: 'edit_transaction',
        defaultMessage: 'Modifier une transaction'
      })
    }
    return intl.formatMessage({
      id: 'create_transaction',
      defaultMessage: 'Créer une transaction'
    })
  }, [data, intl, params.id])

  return (
    <Page>
      <Container size="xl">
        <PageTitle
          hidden
          title={title}
        />

        <BreadcrumbCard>
          <AdminBreadcrumb>
            <AnchorLink
              to={transactionQuery.isFetched
                ? getTransactionListLink(data?.expense)
                : '?'}
            >
              {data?.expense
                ? intl.formatMessage({
                  id: 'expenses',
                  defaultMessage: 'Dépenses'
                })
                : intl.formatMessage({
                  id: 'revenues',
                  defaultMessage: 'Revenus'
                })}
            </AnchorLink>
            <span>{title}</span>
          </AdminBreadcrumb>
        </BreadcrumbCard>

        <Card shadow="sm">
          <LoadingHelper
            query={transactionQuery}
            render={() => (
              <TransactionForm initialValues={data} />
            )}
          />
        </Card>
      </Container>
    </Page>
  )
}

export default TransactionFormPage
